import { db } from '../config/firebase';
import { collection, addDoc, query, where, getDocs, deleteDoc, doc, orderBy, Timestamp, updateDoc } from 'firebase/firestore';

/**
 * Service: waitlistService.js
 * Gerencia a fila de espera de agendamentos
 */

/**
 * Adicionar usuário à fila de espera
 * @param {Object} data - Dados da solicitação
 * @returns {Object} { success: boolean, id?: string, error?: string }
 */
export const addToWaitlist = async (data) => {
  try {
    const { barbershopId, userId, userName, userEmail, userPhone, date, preferredTimes } = data;

    // Validações
    if (!barbershopId || !userId || !userName || !date) {
      return { success: false, error: 'Dados obrigatórios faltando' };
    }

    const waitlistData = {
      barbershopId,
      userId,
      userName,
      userEmail: userEmail || '',
      userPhone: userPhone || '',
      date,
      preferredTimes: preferredTimes || [], // Array de horários preferidos: ['09:00', '10:00']
      status: 'waiting', // 'waiting' | 'notified' | 'cancelled'
      createdAt: Timestamp.now(),
      notifiedAt: null
    };

    const docRef = await addDoc(collection(db, 'waitlist'), waitlistData);
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Erro ao adicionar à fila de espera:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Obter todas as solicitações de fila de espera de uma barbearia
 * @param {string} barbershopId - ID da barbearia
 * @returns {Array} Lista de solicitações
 */
export const getWaitlistByBarbershop = async (barbershopId) => {
  try {
    const q = query(
      collection(db, 'waitlist'),
      where('barbershopId', '==', barbershopId),
      where('status', '==', 'waiting'),
      orderBy('createdAt', 'asc')
    );

    const snapshot = await getDocs(q);
    const waitlist = [];
    
    snapshot.forEach((doc) => {
      waitlist.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      });
    });

    return waitlist;
  } catch (error) {
    console.error('Erro ao buscar fila de espera:', error);
    return [];
  }
};

/**
 * Obter solicitações de fila de espera de um usuário
 * @param {string} userId - ID do usuário
 * @returns {Array} Lista de solicitações do usuário
 */
export const getWaitlistByUser = async (userId) => {
  try {
    const q = query(
      collection(db, 'waitlist'),
      where('userId', '==', userId),
      where('status', '==', 'waiting'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const waitlist = [];
    
    snapshot.forEach((doc) => {
      waitlist.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      });
    });

    return waitlist;
  } catch (error) {
    console.error('Erro ao buscar solicitações do usuário:', error);
    return [];
  }
};

/**
 * Verificar se usuário já está na fila de espera para uma data específica
 * @param {string} barbershopId - ID da barbearia
 * @param {string} userId - ID do usuário
 * @param {string} date - Data no formato YYYY-MM-DD
 * @returns {boolean} true se já está na fila
 */
export const isUserInWaitlist = async (barbershopId, userId, date) => {
  try {
    const q = query(
      collection(db, 'waitlist'),
      where('barbershopId', '==', barbershopId),
      where('userId', '==', userId),
      where('date', '==', date),
      where('status', '==', 'waiting')
    );

    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Erro ao verificar fila de espera:', error);
    return false;
  }
};

/**
 * Cancelar solicitação de fila de espera
 * @param {string} waitlistId - ID da solicitação
 * @returns {Object} { success: boolean }
 */
export const cancelWaitlist = async (waitlistId) => {
  try {
    await deleteDoc(doc(db, 'waitlist', waitlistId));
    return { success: true };
  } catch (error) {
    console.error('Erro ao cancelar fila de espera:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Marcar solicitação como notificada
 * @param {string} waitlistId - ID da solicitação
 * @returns {Object} { success: boolean }
 */
export const markAsNotified = async (waitlistId) => {
  try {
    const waitlistRef = doc(db, 'waitlist', waitlistId);
    await updateDoc(waitlistRef, {
      status: 'notified',
      notifiedAt: Timestamp.now()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Erro ao marcar como notificado:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Obter posição do usuário na fila de espera
 * @param {string} barbershopId - ID da barbearia
 * @param {string} userId - ID do usuário
 * @param {string} date - Data no formato YYYY-MM-DD
 * @returns {Object} { position: number, total: number }
 */
export const getUserPositionInWaitlist = async (barbershopId, userId, date) => {
  try {
    const q = query(
      collection(db, 'waitlist'),
      where('barbershopId', '==', barbershopId),
      where('date', '==', date),
      where('status', '==', 'waiting'),
      orderBy('createdAt', 'asc')
    );

    const snapshot = await getDocs(q);
    const waitlist = [];
    
    snapshot.forEach((doc) => {
      waitlist.push({
        id: doc.id,
        ...doc.data()
      });
    });

    const position = waitlist.findIndex(item => item.userId === userId);
    
    return {
      position: position >= 0 ? position + 1 : 0,
      total: waitlist.length
    };
  } catch (error) {
    console.error('Erro ao buscar posição na fila:', error);
    return { position: 0, total: 0 };
  }
};

/**
 * Remover waitlists notificados há mais de 24 horas
 * @returns {Object} { success: boolean, removed: number }
 */
export const removeNotifiedExpired = async (barbershopId) => {
  try {
    const oneDayAgo = new Date();
    oneDayAgo.setHours(oneDayAgo.getHours() - 24);

    const q = query(
      collection(db, 'waitlist'),
      where('barbershopId', '==', barbershopId),
      where('status', '==', 'notified'),
      where('notifiedAt', '<=', Timestamp.fromDate(oneDayAgo))
    );

    const snapshot = await getDocs(q);
    let removed = 0;

    for (const docSnapshot of snapshot.docs) {
      await deleteDoc(doc(db, 'waitlist', docSnapshot.id));
      removed++;
    }

    if (removed > 0) {
      console.log(`🧹 Removidos ${removed} waitlists notificados expirados`);
    }

    return { success: true, removed };
  } catch (error) {
    console.error('Erro ao remover notificados expirados:', error);
    return { success: false, error: error.message, removed: 0 };
  }
};

/**
 * Remover waitlists com data passada
 * @param {string} barbershopId - ID da barbearia
 * @returns {Object} { success: boolean, removed: number }
 */
export const removeExpiredWaitlists = async (barbershopId) => {
  try {
    const _now = new Date();
    const today = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;

    const q = query(
      collection(db, 'waitlist'),
      where('barbershopId', '==', barbershopId),
      where('date', '<', today)
    );

    const snapshot = await getDocs(q);
    let removed = 0;

    for (const docSnapshot of snapshot.docs) {
      await deleteDoc(doc(db, 'waitlist', docSnapshot.id));
      removed++;
    }

    if (removed > 0) {
      console.log(`🧹 Removidos ${removed} waitlists com data passada`);
    }

    return { success: true, removed };
  } catch (error) {
    console.error('Erro ao remover waitlists expirados:', error);
    return { success: false, error: error.message, removed: 0 };
  }
};

/**
 * Obter waitlist detalhado por barbearia (para owners)
 * @param {string} barbershopId - ID da barbearia
 * @returns {Array} Lista de waitlists agrupados por data
 */
export const getWaitlistDetailsByBarbershop = async (barbershopId) => {
  try {
    const q = query(
      collection(db, 'waitlist'),
      where('barbershopId', '==', barbershopId),
      where('status', 'in', ['waiting', 'notified']),
      orderBy('date', 'asc')
    );

    const snapshot = await getDocs(q);
    const waitlist = [];
    
    snapshot.forEach((doc) => {
      waitlist.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        notifiedAt: doc.data().notifiedAt?.toDate()
      });
    });

    // Agrupar por data
    const grouped = waitlist.reduce((acc, item) => {
      if (!acc[item.date]) {
        acc[item.date] = [];
      }
      acc[item.date].push(item);
      return acc;
    }, {});

    return grouped;
  } catch (error) {
    console.error('Erro ao buscar waitlist detalhado:', error);
    return {};
  }
};
